import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";
import EncryptDecryptService from "../services/EncryptDecryptService";
import { ParentMessageService } from "../services/parentBalanceService";
import { AuthService } from "../services/authService";
import {
  createCurrencyConfig,
  DEFAULT_STAKE_SETTINGS,
  getStakeConfig,
  loadGameConfig,
  StakeSettings,
} from "../services/gameConfigService";
import { CurrencyConfig } from "../config/currencyConfig";
import { socketBaseUrl } from "../config/apiConfig";

// ClientSeed generation function
const generateClientSeed = (): string => {
  if (typeof window !== "undefined" && window.crypto) {
    const arr = new Uint8Array(16);
    window.crypto.getRandomValues(arr);
    return Array.from(arr, (b) => b.toString(16).padStart(2, "0")).join("");
  }

  const fallbackArr = new Array(16);
  for (let i = 0; i < 16; i++) {
    fallbackArr[i] = Math.floor(Math.random() * 256);
  }
  return fallbackArr.map((b) => b.toString(16).padStart(2, "0")).join("");
};

// Types for socket messages
export interface SocketMessage {
  type: string;
  data?: any;
  [key: string]: any;
}

export interface GameState {
  gameId: string;
  status: "waiting" | "playing" | "crashed";
  multiplier: number;
  crashPoint?: number;
  players: Player[];
  timeLeft?: number;
}

export interface Player {
  id: string;
  username: string;
  betAmount: number;
  cashoutMultiplier?: number;
  isWinner?: boolean;
}

export enum Status {
  PLACED = "PLACED",
  CASHOUT = "CASHOUT",
}
export type BetStatus = Status.PLACED | Status.CASHOUT;
export interface Bet {
  betId: string;
  oddsRate: number;
  cashOut: number;
  nation: BetStatus;
  userName?: string;
  eventId?: string;
  eventName?: string;
  roundId?: string;
  stake?: string;
  createdAt?: string;
  avatar?: string;
}

export interface BetResult {
  betId: string;
  status: "pending" | "placed" | "cashed_out" | "lost";
  amount: number;
  multiplier?: number;
  profit?: number;
}

export interface SessionBet {
  sessionId: string;
  betId?: string;
  betType: string;
  roundId: string;
  stake: number;
  placedAt: string;
  multiplier?: number;
  cashout?: number;
  status: "placed" | "cashed_out" | "lost";
}

// Socket event types
export type SocketEventType =
  | "balance_update"
  | "bet_result"
  | "market_update"
  | "auth_success"
  | "auth_failed"
  | "connection_status"
  | "game_update"
  | "user_count";

export enum GameStages {
  WAIT = "WAIT",
  RUN = "RUN",
  BLAST = "BLAST",
}
export type GameStatus = GameStages.WAIT | GameStages.RUN | GameStages.BLAST;

export type GameData = {
  status?: GameStatus;
  startsIn?: number;
  multiplier?: number;
  crashPoint?: number;
};

// Socket provider context
interface SocketContextType {
  isConnected: boolean;
  isAuthenticated: boolean;
  balance: number;
  currency: string;
  currencyConfig: CurrencyConfig;
  gameConfigReady: boolean;
  marketData: any;
  lastBetResult: any;
  connect: (url?: string, messageToSocket?: any) => void;
  disconnect: () => void;
  sendMessage: (message: any) => void;
  subscribe: (
    eventType: SocketEventType,
    callback: (data: any) => void,
  ) => () => void;
  unsubscribe: (
    eventType: SocketEventType,
    callback: (data: any) => void,
  ) => void;
  needToSendPrevious: () => boolean;
  encryptionState: {
    hasKeyPair: boolean;
    hasServerKey: boolean;
    hasEncryptionKey: boolean;
  };
  getMarketData: () => {
    subscribe: (callback: (data: any) => void) => () => void;
  };
  gameData: GameData;
  bets: Bet[];
  sessionBetHistory: SessionBet[];
  username: string;
  userCount: number;
  eventId: string;
  roundId: number;
  clientSeed: string;
  generateNewSeed: () => string;
  changeClientSeed: (seed: string) => void;
  serverSeedHash: string;
  refreshConnection: () => void;
  getPublicKeyBase64?: () => string | null;
  isEncryptionEnabled?: boolean;
}

const SocketContext = createContext<SocketContextType | undefined>(undefined);

// Avatar generation function
function generateUserAvatar(userName: string) {
  let hash = 0;
  for (let i = 0; i < userName.length; i++) {
    const char = userName.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash;
  }
  const avatarIndex = Math.abs(hash % 72) + 1;
  return `/avatars/av-${avatarIndex}.png`;
}

const extractServerSeedHash = (payload: any): string => {
  const candidates = [
    payload,
    payload?.data,
    payload?.provablyFair,
    payload?.provably_fair,
    payload?.fairness,
    payload?.data?.provablyFair,
    payload?.data?.provably_fair,
  ];
  const keys = [
    "serverSeedHash",
    "server_seed_hash",
    "serverHash",
    "server_hash",
    "seedHash",
    "seed_hash",
  ];

  for (const candidate of candidates) {
    if (!candidate || typeof candidate !== "object") continue;

    for (const key of keys) {
      const value = candidate[key];
      if (typeof value === "string" && value.trim()) return value.trim();
    }
  }

  return "";
};

// Socket Provider Component
export const SocketProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [clientSeed, setClientSeed] = useState<string>(() => {
    if (typeof window !== "undefined") {
      const savedSeed = localStorage.getItem("socket_client_seed");
      if (savedSeed) return savedSeed;
    }
    return generateClientSeed();
  });

  const [isConnected, setIsConnected] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [balance, setBalance] = useState(0);
  const [currency, setCurrency] = useState(() => AuthService.getCurrency());
  const [stakeSettings, setStakeSettings] = useState<StakeSettings>(
    DEFAULT_STAKE_SETTINGS,
  );
  const [gameConfigReady, setGameConfigReady] = useState(false);
  const [marketData, setMarketData] = useState<any>(null);
  const [lastBetResult, setLastBetResult] = useState<any>(null);
  const [gameData, setGameData] = useState<GameData>({});
  const [bets, setBets] = useState<Bet[]>([]);
  const [sessionBetHistory, setSessionBetHistory] = useState<SessionBet[]>([]);
  const [username, setUsername] = useState(() => AuthService.getUsername());
  const [userCount, setUserCount] = useState<number>(0);
  const [eventId] = useState(() => AuthService.getEventId());
  const [roundId, setRoundId] = useState(0);
  const [serverSeedHash, setServerSeedHash] = useState("");

  const socketRef = useRef<WebSocket | null>(null);
  const subscribersRef = useRef<Map<SocketEventType, Set<(data: any) => void>>>(
    new Map(),
  );
  const reconnectAttemptsRef = useRef(0);
  const reconnectInterval = 5000;
  const isReconnectingRef = useRef(false);
  const isAttemptRef = useRef(false);
  const previousMsgRef = useRef<any>(null);
  const previousRoundRef = useRef<string>("");
  const urlRef = useRef<string>("");
  const usernameRef = useRef(username);
  const ownBetIdsRef = useRef(new Set<string>());

  const pingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const encryptDecryptServiceRef = useRef<EncryptDecryptService>(
    new EncryptDecryptService(),
  );

  useEffect(() => {
    const eventTypes: SocketEventType[] = [
      "balance_update",
      "bet_result",
      "market_update",
      "auth_success",
      "auth_failed",
      "connection_status",
      "game_update",
      "user_count",
    ];

    eventTypes.forEach((type) => {
      if (!subscribersRef.current.has(type)) {
        subscribersRef.current.set(type, new Set());
      }
    });

    encryptDecryptServiceRef.current.getMessageFromSocket();

    // Initialize parent message service for iframe communication
    ParentMessageService.initialize();

    // Subscribe to balance updates from parent window
    const unsubscribe = ParentMessageService.onBalanceUpdate(
      (newBalance: number, newCurrency: string) => {
        if (newBalance >= 0) {
          setBalance(newBalance);
        }
        if (newCurrency) {
          AuthService.setCurrency(newCurrency);
          setCurrency(AuthService.getCurrency());
        }
      },
    );

    const unsubscribeUserDetails =
      ParentMessageService.onUserDetailsUpdate((userDetails) => {
        AuthService.setUserDetails(userDetails);
        const nextUsername = AuthService.getUsername();
        usernameRef.current = nextUsername;
        setUsername(nextUsername);
      });

    // Request initial balance from parent
    ParentMessageService.requestBalance();
    ParentMessageService.requestUserDetails();

    // Notify parent that game is ready
    ParentMessageService.notifyGameReady();

    return () => {
      unsubscribe();
      unsubscribeUserDetails();
      ParentMessageService.destroy();
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    loadGameConfig().then((config) => {
      if (cancelled) return;

      setStakeSettings(config.stakeSettings);
      if (config.currency) {
        AuthService.setCurrency(config.currency);
        setCurrency(AuthService.getCurrency());
      }
      setGameConfigReady(true);
    });

    return () => {
      cancelled = true;
    };
  }, []);

  const stakeConfig = useMemo(
    () => getStakeConfig(stakeSettings, currency),
    [currency, stakeSettings],
  );
  const currencyConfig = useMemo(
    () => createCurrencyConfig(currency, stakeConfig),
    [currency, stakeConfig],
  );

  // Save clientSeed to localStorage when it changes
  useEffect(() => {
    if (typeof window !== "undefined" && clientSeed) {
      localStorage.setItem("socket_client_seed", clientSeed);
    }
  }, [clientSeed]);

  // Generate new client seed
  const generateNewClientSeed = useCallback((): string => {
    const newSeed = generateClientSeed();
    setClientSeed(newSeed);

    return newSeed;
  }, []);

  const changeClientSeed = useCallback((seed: string) => {
    const normalizedSeed = seed.trim();
    if (!normalizedSeed) return;

    localStorage.setItem("socket_client_seed", normalizedSeed);
    setClientSeed(normalizedSeed);
  }, []);

  const generateSocketUrl = useCallback(
    (eventIdParam?: string): string => {
      // Get values
      const seed = clientSeed || generateClientSeed();
      const token = AuthService.getToken();
      const storedEventId = localStorage.getItem("eventId");
      const rawEventId = eventIdParam || storedEventId || "event-1";
      const normalizedEventId = rawEventId.startsWith("event-")
        ? rawEventId
        : `event-${rawEventId}`;

      // Validate
      if (!token) {
        return "";
      }

      if (!seed) {
        return "";
      }

      // Build URL
      const url = `wss://${socketBaseUrl}/${normalizedEventId}?token=${token}&clientSeed=${seed}`;
      // const url = `wss://aviatorengine.fly.dev/websocket/9112?clientSeed=seed&uid=abcd&name=testingUser`;

      return url;
    },
    [clientSeed],
  );

  // Notify subscribers
  const notifySubscribers = useCallback(
    (eventType: SocketEventType, data: any) => {
      const subscribers = subscribersRef.current.get(eventType);
      if (subscribers) {
        subscribers.forEach((callback) => {
          try {
            callback(data);
          } catch {}
        });
      }
    },
    [],
  );

  // In SocketProvider component, update the handleMessage function:
  const handleMessage = useCallback(
    async (event: MessageEvent) => {
      try {
        const processedData =
          await encryptDecryptServiceRef.current.processMessage(
            event.data,
            socketRef.current,
          );

        if (!processedData) return;

        const emitEvent = (data: any) => {
          const nextServerSeedHash = extractServerSeedHash(data);
          if (nextServerSeedHash) {
            setServerSeedHash(nextServerSeedHash);
          } else if (data?.p === "WAIT") {
            setServerSeedHash("");
          }

          if (
            data?.status === "error" &&
            typeof data?.message === "string" &&
            data.message.includes("Insufficient balance")
          ) {
            if (typeof window !== "undefined" && window.parent) {
              window.parent.postMessage("Insufficient balance", "*");
            }
          }

          if (typeof data?.tcu === "number") {
            setUserCount(data.tcu);
            notifySubscribers("user_count", data);
            return;
          }

          const responseType = String(data?.type ?? data?.t ?? "");
          if (
            data?.status !== "error" &&
            ["3", "4", "7"].includes(responseType)
          ) {
            ParentMessageService.requestBalance();
          }

          const isCashoutPayload =
            (data?.nation === Status.CASHOUT ||
              data?.status === Status.CASHOUT) &&
            (data?.betId || data?.betid);

          if (isCashoutPayload) {
            handleCashoutUpdate(data);
            if (!data.type) {
              return;
            }
          }

          if (!data.type) {
            handleGameData(data);
            return;
          }
          switch (data.type) {
            case "auth_success":
              setIsAuthenticated(true);
              reconnectAttemptsRef.current = 0;
              notifySubscribers("auth_success", data);
              break;

            case "auth_failed":
              setIsAuthenticated(false);
              if (socketRef.current) {
                socketRef.current.close(1000, "Auth failed");
                socketRef.current = null;
              }
              notifySubscribers("auth_failed", data);
              break;

            case "balance_update":
              setBalance(data.balance);
              notifySubscribers("balance_update", data);
              break;

            case "bet_result":
              setLastBetResult(data);
              notifySubscribers("bet_result", data);
              break;

            case "3":
              // Check if this is a bet confirmation (has bets array) or user count
              if (data.status === "success" && data.bets) {
                // ✅ ADD: Format and add bets to bets array for BetBoard
                const formattedBets = data.bets.map((bet: any) => {
                  const isCashout =
                    bet.nation === Status.CASHOUT ||
                    bet.status === Status.CASHOUT;
                  const oddsRate =
                    parseFloat(
                      bet.oddsRate ?? bet.multiplier ?? bet.RUNValue ?? 1,
                    ) || 1;
                  const cashOut =
                    parseFloat(bet.cashOut ?? bet.cashout ?? 0) || 0;

                  if (bet.betId) ownBetIdsRef.current.add(String(bet.betId));

                  return {
                    betId: bet.betId,
                    userName: usernameRef.current,
                    avatar: generateUserAvatar(usernameRef.current),
                    stake: bet.stake,
                    oddsRate,
                    cashOut,
                    nation: isCashout ? Status.CASHOUT : Status.PLACED,
                    status: isCashout ? Status.CASHOUT : Status.PLACED,
                    roundId: bet.round,
                    betType: bet.betType,
                    createdAt: bet.createdAt || new Date().toISOString(),
                    eventId: bet.eventId || "88.0022",
                  };
                });

                setBets((prev) => {
                  const next = [...prev];
                  formattedBets.forEach((bet: any) => {
                    const existingIndex = next.findIndex(
                      (item) => item.betId === bet.betId,
                    );
                    if (existingIndex >= 0) {
                      next[existingIndex] = { ...next[existingIndex], ...bet };
                    } else {
                      next.push(bet);
                    }
                  });
                  return next;
                });

                setSessionBetHistory((previous) => {
                  const next = [...previous];

                  formattedBets.forEach((bet: any) => {
                    const roundKey = String(bet.roundId || "");
                    const betType = String(bet.betType || "1");
                    const existingIndex = next.findIndex(
                      (item) =>
                        item.betId === bet.betId ||
                        (item.roundId === roundKey &&
                          item.betType === betType),
                    );

                    if (existingIndex >= 0) {
                      next[existingIndex] = {
                        ...next[existingIndex],
                        betId: bet.betId,
                        stake: Number(bet.stake) || next[existingIndex].stake,
                      };
                    } else {
                      next.unshift({
                        sessionId: `session-${roundKey}-${betType}-${Date.now()}`,
                        betId: bet.betId,
                        betType,
                        roundId: roundKey,
                        stake: Number(bet.stake) || 0,
                        placedAt: bet.createdAt || new Date().toISOString(),
                        status: "placed",
                      });
                    }
                  });

                  return next;
                });

                notifySubscribers("bet_result", data);
              } else {
                setUserCount(data.user_count || 0);
              }
              break;

            case "place_bet":
            case "PlaceBet":
              handleBetPlacement(data);
              break;

            // ✅ ADD: Handle cashout messages from server
            case "cashout_success":
            case "CashoutBet":
              handleCashoutUpdate(data);
              break;

            default:
              break;
          }
        };

        const handleGameData = (data: any) => {
          const { rv, tl, p, cv, action, r, placeBets } = data;

          // ✅ ADD: Handle placeBets from game data
          if (placeBets && Array.isArray(placeBets)) {
            const formattedPlaceBets = placeBets.map((bet: any) => {
              const isOwnBet =
                !!bet.betId && ownBetIdsRef.current.has(String(bet.betId));
              const displayName = isOwnBet
                ? usernameRef.current
                : bet.clientName || `Player${bet.betType}`;

              return {
                betId: bet.betId,
                userName: displayName,
                avatar: generateUserAvatar(displayName),
                stake: bet.stake,
                oddsRate: 1.0,
                cashOut: 0,
                nation: Status.PLACED,
                roundId: bet.round,
                betType: bet.betType,
                createdAt: bet.createdAt || new Date().toISOString(),
                eventId: bet.eventId || "88.0022",
              };
            });

            setBets((prev) => {
              const next = [...prev];
              formattedPlaceBets.forEach((bet: any) => {
                const existingIndex = next.findIndex(
                  (item) => item.betId === bet.betId,
                );
                if (existingIndex >= 0) {
                  next[existingIndex] = { ...next[existingIndex], ...bet };
                } else {
                  next.push(bet);
                }
              });
              return next;
            });
          }

          // ✅ ADD: Handle cashout messages in game data
          if (action === "CashoutBet") {
            handleCashoutUpdate(data);
            return;
          }

          // Handle WAIT phase
          if (p === "WAIT") {
            if (r) {
              const nextRound = String(r);
              if (
                previousRoundRef.current &&
                previousRoundRef.current !== nextRound
              ) {
                window.setTimeout(
                  () => ParentMessageService.requestBalance(),
                  4300,
                );
              }
              previousRoundRef.current = nextRound;
              setRoundId(r);
            }
            setGameData((prev) => ({
              ...prev,
              status: GameStages.WAIT,
              startsIn: typeof tl === "number" ? tl : parseInt(tl) || 0,
              multiplier: 1.0,
            }));
          }
          // Handle RUN phase (multiplier updates)
          else if (rv !== undefined && rv !== null) {
            const multiplier = parseFloat(rv) || 1.0;
            setGameData((prev) => ({
              ...prev,
              status: GameStages.RUN,
              multiplier: multiplier,
              startsIn: 0,
            }));
          }
          // Handle CRASH phase
          else if (p === "CRASH") {
            const crashPoint = parseFloat(cv) || 1.0;

            setGameData((prev) => ({
              ...prev,
              status: GameStages.BLAST,
              crashPoint: crashPoint,
              multiplier: crashPoint,
            }));
            setSessionBetHistory((previous) =>
              previous.map((bet) =>
                bet.status === "placed"
                  ? { ...bet, status: "lost", multiplier: crashPoint }
                  : bet,
              ),
            );
          }
          // Handle explicit RUN phase indicator
          else if (p === "RUN") {
            setGameData((prev) => ({
              ...prev,
              status: GameStages.RUN,
              startsIn: 0,
            }));
          }
        };

        // ✅ NEW FUNCTION FOR BET PLACEMENT
        const handleBetPlacement = (betData: any) => {
          // Format: {"action":"PlaceBet","round":"260115103028","stake":0.5,"betType":"1"}
          const newBet = {
            betId: `temp-bet-${Date.now()}-${Math.random()
              .toString(36)
              .substr(2, 9)}`,
            userName: usernameRef.current,
            stake: betData.stake || 0.5,
            oddsRate: 1.0, // Initial odds
            cashOut: 0,
            nation: Status.PLACED,
            avatar: generateUserAvatar(usernameRef.current),
            roundId: betData.round || "unknown",
            betType: betData.betType || "1", // "1" ya "2"
            createdAt: new Date().toISOString(),
            eventId: betData.eventId || "88.0022",
          };
          setBets((prev) => [...prev, newBet]);

          // Update balance (deduct stake)
          setBalance((prev) => Math.max(0, prev - (betData.stake || 0)));

          // Notify subscribers
          notifySubscribers("bet_result", {
            type: "bet_placed",
            bet: newBet,
            success: true,
          });
        };

        // ✅ NEW FUNCTION FOR CASHOUT UPDATES
        const handleCashoutUpdate = (data: any) => {
          const {
            betId,
            betid,
            RUNValue,
            multiplier,
            cashOut,
            cashout,
            stake,
            clientName,
            betType,
            round,
          } = data;

          const finalBetId = betId || betid;
          const finalMultiplier = parseFloat(multiplier || RUNValue || 1) || 1;
          const finalCashOut = cashOut ?? cashout;

          if (!finalBetId) return;

          setBets((prev: any) => {
            const existingIndex = prev.findIndex(
              (bet: any) => bet.betId === finalBetId,
            );

            const updateBet = (bet: any) => {
              const winAmount =
                finalCashOut ??
                (bet?.stake ? parseFloat(bet.stake) * finalMultiplier : 0);

              return {
                ...bet,
                nation: "CASHOUT",
                status: "CASHOUT",
                oddsRate: finalMultiplier,
                cashOut: winAmount,
                isWinner: true,
              };
            };

            if (existingIndex >= 0) {
              const next = [...prev];
              next[existingIndex] = updateBet(next[existingIndex]);
              return next;
            }

            const isOwnBet = ownBetIdsRef.current.has(String(finalBetId));
            const displayName = isOwnBet
              ? usernameRef.current
              : clientName || `Player${betType || "1"}`;
            const placeholderBet = updateBet({
              betId: finalBetId,
              userName: displayName,
              avatar: generateUserAvatar(displayName),
              stake: stake || 0,
              roundId: round || "unknown",
              betType: betType || "1",
              createdAt: new Date().toISOString(),
              eventId: data.eventId || "88.0022",
            });

            return [...prev, placeholderBet];
          });

          setSessionBetHistory((previous) =>
            previous.map((bet) => {
              const matchesBetId = !!finalBetId && bet.betId === finalBetId;
              const matchesRoundAndType =
                !!round &&
                String(round) === bet.roundId &&
                (!betType || String(betType) === bet.betType);

              if (!matchesBetId && !matchesRoundAndType) return bet;

              const resolvedCashout = Number(
                finalCashOut ?? bet.stake * finalMultiplier,
              );
              return {
                ...bet,
                betId: finalBetId || bet.betId,
                multiplier: finalMultiplier,
                cashout: Number.isFinite(resolvedCashout)
                  ? resolvedCashout
                  : undefined,
                status: "cashed_out",
              };
            }),
          );

          notifySubscribers("bet_result", {
            type: "cashout",
            betId: finalBetId,
          });
        };

        if (Array.isArray(processedData)) {
          processedData.forEach((item) => {
            if (item && typeof item === "object") {
              emitEvent(item);
            }
          });
        } else {
          emitEvent(processedData);
        }
      } catch {}
    },
    [notifySubscribers],
  );

  const startPing = useCallback(() => {
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
    }

    pingIntervalRef.current = setInterval(() => {
      if (socketRef.current?.readyState === WebSocket.OPEN) {
        socketRef.current.send(JSON.stringify({ type: "ping" }));
      }
    }, 60000);
  }, []);

  // Stop ping interval
  const stopPing = useCallback(() => {
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }
  }, []);

  const connect = useCallback(
    async (url?: string, messageToSocket?: any, eventIdParam?: string) => {
      if (socketRef.current) {
        const state = socketRef.current.readyState;
        if (state === WebSocket.CONNECTING) {
          return;
        }
        if (state === WebSocket.OPEN) {
          return;
        }
        if (state === WebSocket.CLOSING) {
          setTimeout(() => connect(url, messageToSocket, eventIdParam), 1000);
          return;
        }
      }

      // ✅ Prevent multiple reconnection attempts
      if (isReconnectingRef.current) {
        return;
      }

      try {
        isReconnectingRef.current = true;

        if (url) {
          urlRef.current = url;
        } else {
          // Generate new clientSeed if not already set
          if (!clientSeed) {
            generateNewClientSeed();
          }

          const generatedUrl = generateSocketUrl(eventIdParam);

          if (!generatedUrl) {
            isReconnectingRef.current = false;
            return;
          }

          urlRef.current = generatedUrl;
        }

        // ✅ ALWAYS use direct WebSocket (no encryption for new server)

        socketRef.current = new WebSocket(urlRef.current);

        // Set up event handlers
        socketRef.current.onopen = () => {
          setIsConnected(true);
          setIsAuthenticated(false);
          isReconnectingRef.current = false;
          isAttemptRef.current = true;
          reconnectAttemptsRef.current = 0;

          notifySubscribers("connection_status", { status: "connected" });
          startPing();

          // Send initial message if needed
          setTimeout(() => {
            if (socketRef.current && isAttemptRef.current) {
              if (!previousMsgRef.current) {
                previousMsgRef.current = { type: "2", id: "" };
              }
            }
          }, 1000);
        };

        socketRef.current.onmessage = handleMessage;

        socketRef.current.onclose = (event) => {
          setIsConnected(false);
          setIsAuthenticated(false);
          isReconnectingRef.current = false;
          isAttemptRef.current = false;
          stopPing();

          notifySubscribers("connection_status", {
            status: "disconnected",
            code: event.code,
            reason: event.reason,
            wasClean: event.wasClean,
          });

          // Auto-reconnect if not manual disconnect (code 1000) or normal closure
          if (event.code !== 1000 && !event.wasClean) {
            setTimeout(() => {
              if (!isReconnectingRef.current) {
                // Generate new seed for reconnect
                generateNewClientSeed();
                connect(); // Recursive call
              }
            }, reconnectInterval);
          } else {
          }
        };

        socketRef.current.onerror = (error) => {
          isReconnectingRef.current = false;
          notifySubscribers("connection_status", {
            status: "error",
            error: error,
          });
        };
      } catch {
        isReconnectingRef.current = false;
      }
    },
    [
      clientSeed,
      generateSocketUrl,
      generateNewClientSeed,
      handleMessage,
      notifySubscribers,
      startPing,
      stopPing,
    ],
  );

  // Disconnect from WebSocket
  const disconnect = useCallback(() => {
    stopPing();
    isReconnectingRef.current = false;
    reconnectAttemptsRef.current = 0;
    isAttemptRef.current = false;

    // Close encryption service
    encryptDecryptServiceRef.current.closeExistingSocket();

    if (socketRef.current) {
      socketRef.current.close(1000, "Manual disconnect");
      socketRef.current = null;
    }

    setIsConnected(false);
    setIsAuthenticated(false);
    setBalance(0);
    setMarketData(null);
    setLastBetResult(null);
  }, [stopPing]);

  // Send message through WebSocket
  const sendMessage = useCallback((message: any) => {
    if (!socketRef.current) {
      return;
    }

    isAttemptRef.current = false;
    previousMsgRef.current = message;

    const actions = Array.isArray(message) ? message : [message];
    setSessionBetHistory((previous) => {
      let next = [...previous];

      actions.forEach((action: any) => {
        if (action?.action === "PlaceBet") {
          const roundKey = String(action.round || "");
          const betType = String(action.betType || "1");
          const existingIndex = next.findIndex(
            (bet) => bet.roundId === roundKey && bet.betType === betType,
          );

          if (existingIndex >= 0) {
            next[existingIndex] = {
              ...next[existingIndex],
              stake: Number(action.stake) || next[existingIndex].stake,
            };
          } else {
            next.unshift({
              sessionId: `session-${roundKey}-${betType}-${Date.now()}-${Math.random()
                .toString(36)
                .slice(2, 8)}`,
              betType,
              roundId: roundKey,
              stake: Number(action.stake) || 0,
              placedAt: new Date().toISOString(),
              status: "placed",
            });
          }
        }

        if (action?.action === "CashoutBet") {
          const multiplier = Number(action.RUNValue);
          next = next.map((bet) => {
            const matchesBetId = action.betid && bet.betId === action.betid;
            const matchesRoundAndType =
              String(action.round || "") === bet.roundId &&
              String(action.betType || "") === bet.betType;

            if (!matchesBetId && !matchesRoundAndType) return bet;

            return {
              ...bet,
              multiplier: Number.isFinite(multiplier)
                ? multiplier
                : bet.multiplier,
              cashout: Number.isFinite(multiplier)
                ? bet.stake * multiplier
                : bet.cashout,
              status: "cashed_out",
            };
          });
        }

        if (action?.action === "CancelBet") {
          next = next.filter((bet) => {
            const matchesBetId = action.betId && bet.betId === action.betId;
            const matchesRoundAndType =
              String(action.round || "") === bet.roundId &&
              String(action.betType || "") === bet.betType;
            return !matchesBetId && !matchesRoundAndType;
          });
        }
      });

      return next;
    });

    let messageToSend: string;

    // Check if encryption is available
    const encryptionState =
      encryptDecryptServiceRef.current.getEncryptionState();
    if (encryptionState.hasEncryptionKey) {
      // Send encrypted message
      messageToSend =
        encryptDecryptServiceRef.current.sendMessageToSocket(message);
    } else {
      // Send plain JSON message
      messageToSend = JSON.stringify(message);
    }

    if (socketRef.current.readyState === WebSocket.OPEN) {
      socketRef.current.send(messageToSend);
    } else {
      // Queue message for when socket opens
      socketRef.current.onopen = () => {
        if (socketRef.current?.readyState === WebSocket.OPEN) {
          socketRef.current.send(messageToSend);
        }
      };
    }
  }, []);

  // Check if need to send previous message
  const needToSendPrevious = useCallback(() => {
    return !!(
      socketRef.current &&
      isAttemptRef.current &&
      previousMsgRef.current
    );
  }, []);

  // Subscribe to socket events
  const subscribe = useCallback(
    (eventType: SocketEventType, callback: (data: any) => void) => {
      const subscribers = subscribersRef.current.get(eventType);
      if (subscribers) {
        subscribers.add(callback);
      }

      // Return unsubscribe function
      return () => {
        const subscribers = subscribersRef.current.get(eventType);
        if (subscribers) {
          subscribers.delete(callback);
        }
      };
    },
    [],
  );

  // Unsubscribe from socket events
  const unsubscribe = useCallback(
    (eventType: SocketEventType, callback: (data: any) => void) => {
      const subscribers = subscribersRef.current.get(eventType);
      if (subscribers) {
        subscribers.delete(callback);
      }
    },
    [],
  );

  // Get market data
  const getMarketData = useCallback(() => {
    return encryptDecryptServiceRef.current.getMarketData();
  }, []);

  // Auto-connect on mount
  useEffect(() => {
    if (clientSeed) {
      // Small delay to ensure everything is initialized
      const timer = setTimeout(() => {
        connect();
      }, 100);

      return () => {
        clearTimeout(timer);
        disconnect();
      };
    }
  }, [clientSeed, connect, disconnect]);

  // Context value
  const contextValue: SocketContextType = {
    isConnected,
    isAuthenticated,
    balance,
    currency,
    currencyConfig,
    gameConfigReady,
    marketData,
    lastBetResult,
    connect: (url?: string, messageToSocket?: any) =>
      connect(url, messageToSocket),
    disconnect,
    sendMessage,
    subscribe,
    unsubscribe,
    needToSendPrevious,
    encryptionState: encryptDecryptServiceRef.current.getEncryptionState(),
    getMarketData,
    gameData,
    bets,
    sessionBetHistory,
    username,
    userCount,
    eventId,
    roundId,
    clientSeed,
    generateNewSeed: generateNewClientSeed,
    changeClientSeed,
    serverSeedHash,
    refreshConnection: () => {
      generateNewClientSeed();
      disconnect();
      setTimeout(() => connect(), 1000);
    },
    getPublicKeyBase64: () => {
      try {
        const service = encryptDecryptServiceRef.current as any;
        if (typeof service.getPublicKeyBase64 === "function") {
          return service.getPublicKeyBase64();
        }
        return null;
      } catch {
        return null;
      }
    },
    isEncryptionEnabled:
      encryptDecryptServiceRef.current.getEncryptionState().hasEncryptionKey,
  };

  return (
    <SocketContext.Provider value={contextValue}>
      {children}
    </SocketContext.Provider>
  );
};

// Hook to use socket context
export const useSocket = () => {
  const context = useContext(SocketContext);
  if (context === undefined) {
    throw new Error("useSocket must be used within a SocketProvider");
  }
  return context;
};

// Hook for specific socket events
export const useSocketEvent = (
  eventType: SocketEventType,
  callback: (data: any) => void,
) => {
  const { subscribe, unsubscribe } = useSocket();

  useEffect(() => {
    const unsubscribeFn = subscribe(eventType, callback);
    return () => {
      unsubscribeFn();
      unsubscribe(eventType, callback);
    };
  }, [eventType, callback, subscribe, unsubscribe]);
};

export default SocketProvider;
